import React, { useState } from 'react';
import { CSSTransition as ReactCSSTransitionGroup } from 'react-transition-group';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

import { ReactComponent as Close } from '../common/icons/close.svg';
import Iframe from './components/Iframe';
import Offer from './components/Offer';
import Launcher from './components/Launcher';
import ToggleIcon from './components/elements/ToggleIcon';
import Headings from './components/elements/Headings';
import { CurrencyContextProvider } from './contexts/CurrencyContext';
import { OffersContextProvider } from './contexts/OffersContext';
import { CartContextProvider } from './contexts/CartContext';
import { ProductsContextProvider, useOffers } from './contexts/ProductsContext';
import Content from './components/Content';
import DiscountCodeScreen from './components/DiscountCodeScreen';
import useLocalStorage from './utils/useLocalStorage';
import Footer from './components/Footer';
import { PreviewContextProvider } from './contexts/PreviewContext';
import handleConfirmation from './utils/adaHandler';

const Container = styled.div`
  position: fixed;
  width: 0px;
  height: 0px;
  bottom: 0px;
  right: 0px;
  z-index: 2147483647;
`;

function lastOrDefault(array, cond, defaultValue) {
  if (!array.length) return defaultValue;
  if (!cond) return array[array.length - 1];

  for (var i = array.length - 1; i >= 0; --i) {
    if (cond(array[i])) return array[i];
  }

  return defaultValue;
}

function visibleOffers(offers, settings) {
  if (!settings.gift_only_highest_gift) {
    return offers;
  }
  const sortedOffers = offers.sort(
    (a, b) => a.sortingThreshold - b.sortingThreshold,
  );
  const lastAddedOfferIndex = sortedOffers.indexOf(
    lastOrDefault(sortedOffers, (offer) => offer.added),
  );
  if (lastAddedOfferIndex > 0) {
    return sortedOffers.slice(lastAddedOfferIndex);
  }
  return offers;
}

const BoxContainer = styled.div.attrs(() => ({
  className: 'GiftBox-BoxContainer',
}))`
  z-index: 2147483000 !important;
  position: fixed !important;
  ${(props) => (props.theme.alignLeft ? 'left' : 'right')}: 20px;
  width: 376px !important;
  box-shadow: 0 5px 40px rgba(0, 0, 0, 0.16),
    inset 0 5px 0 0 ${(props) => props.theme.primaryColor} !important;
  border-radius: 8px !important;
  padding-top: 5px;
  overflow: hidden !important;
  opacity: 1 !important;
  background: #fff;
  bottom: calc(200px);
  height: calc(100vh - 200px - 20px);
  min-height: ${({ changeMinHeight }) => (changeMinHeight ? '240px' : 'auto')};
  max-height: ${({ contentHeight }) => contentHeight}px !important;
  -webkit-overflow-scrolling: touch;

  iframe {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
  }

  &.popup-frame-enter {
    opacity: 0 !important;
    transform: translateY(20px) !important;
  }

  &.popup-frame-enter.popup-frame-enter-active,
  &.popup-frame-leave.popup-frame-leave-active {
    opacity: 1 !important;
    transform: translateY(0) !important;
    transition: opacity 250ms ease-out, transform 250ms ease-out;
  }

  &.popup-frame-leave {
    opacity: 1 !important;
  }

  &.popup-frame-leave.popup-frame-leave-active {
    opacity: 0 !important;
    transform: translateY(20px) !important;
  }

  @media screen and (max-width: 450px) and (orientation: portrait),
    screen and (max-width: 736px) and (orientation: landscape) {
    overflow: scroll !important;
    width: calc(100% - 40px) !important;
    z-index: 2147483001 !important;
    left: 20px !important;
    right: 20px !important;
    bottom: 120px !important;
  }
  @media only screen and (max-height: 428px) {
    top: 40px !important;
    bottom: 40px !important;
    height: calc(100vh - 80px) !important;
  }
`;

const FrameCloseIconContainer = styled.div`
  z-index: 2147483003;
  position: absolute;
  width: 40px;
  height: 40px;
  top: 0;
  right: 0;
  background-position: center;
  margin: 12px 8px 12px 2px;
  cursor: pointer;
  display: block;
`;

const FooterContent = styled.div`
  min-height: 100%;
  display: grid;
  grid-template-rows: 1fr auto;
`;

const FrameCloseIcon = styled(ToggleIcon)`
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transform: translateZ(0);
  background-color: rgba(0, 0, 0, 0.05);
  color: #777;
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.6);
  right: 0;
  bottom: 0;
  display: none;
`;

const App = ({ tracker }) => {
  const [open, setOpen] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('offers');

  const toggle = () => {
    setCurrentScreen('offers');
    setOpen(!open);
  };
  const [contentHeight, setContentHeight] = useState(450);
  const [discountCode, setDiscountCode] = useLocalStorage('giftbox-discount');

  const [{ offers, settings, remainingOffers }, updateCartCallback] = useOffers(
    discountCode,
    setOpen,
    tracker,
  );

  if (!offers || !settings || offers.length === 0) {
    return null;
  }

  const theme = {
    primaryColor: settings.primary_color,
    alignLeft: settings.align_left,
  };

  const CustomCss = createGlobalStyle`${settings.custom_css}`;
  const hasAddedOffer = offers.find((o) => o.added);

  const handleOnDiscountCodeChanged = async (discountCode) => {
    setDiscountCode(discountCode);
    updateCartCallback && (await updateCartCallback(discountCode));
  };

  // For offers with no title and subtitle
  const determineMinHeightDiscount = () =>
    currentScreen === 'discount' && !settings.title && !settings.subtitle;

  return (
    <OffersContextProvider offers={offers}>
      {open && offers.find((o) => o.justAdded) && (
        <Backdrop onClick={toggle} className="GiftBox-Backdrop">
          <p> </p> {/* Avoiding global :empty selector */}
        </Backdrop>
      )}
      <ReactCSSTransitionGroup
        in={open}
        timeout={250}
        data-cy="popup-container"
        classNames="popup-frame"
      >
        <ThemeProvider theme={theme}>
          {open && (
            <BoxContainer
              key="container"
              offerCount={offers.length}
              title={settings.title}
              subtitle={settings.subtitle}
              contentHeight={contentHeight}
              changeMinHeight={determineMinHeightDiscount()}
            >
              <Iframe
                data-cy="popup"
                className={hasAddedOffer ? 'has-gift' : ''}
                tabIndex={1}
              >
                <Content heightChanged={setContentHeight}>
                  {currentScreen === 'offers' && (
                    <>
                      <FooterContent>
                        <div>
                          <Headings
                            title={settings.title}
                            subtitle={settings.subtitle}
                          >
                            <FrameCloseIconContainer className="GiftBox-Close">
                              <FrameCloseIcon
                                data-cy="close-popup"
                                open={open}
                                onClick={toggle}
                                onKeyPress={handleConfirmation(toggle)}
                                tabIndex={0}
                              >
                                <Close />
                              </FrameCloseIcon>
                            </FrameCloseIconContainer>
                          </Headings>
                          {visibleOffers(offers, settings).map((offer) => (
                            <Offer key={offer.id} offer={offer} />
                          ))}
                        </div>
                        {settings.discounts_enabled && (
                          <Footer
                            discountCode={discountCode}
                            setCurrentScreen={setCurrentScreen}
                            settings={settings}
                          />
                        )}
                      </FooterContent>
                    </>
                  )}
                  {currentScreen === 'discount' && (
                    <DiscountCodeScreen
                      discountCode={discountCode}
                      onDiscountCodeChanged={handleOnDiscountCodeChanged}
                      setCurrentScreen={setCurrentScreen}
                      settings={settings}
                    />
                  )}
                  <CustomCss />
                </Content>
              </Iframe>
            </BoxContainer>
          )}
        </ThemeProvider>
      </ReactCSSTransitionGroup>
      <Launcher
        theme={theme}
        remainingOffers={remainingOffers}
        tracker={tracker}
        open={open}
        toggle={toggle}
        offers={offers}
        settings={settings}
        hasAddedOffer={hasAddedOffer}
      />
      <CustomCss />
    </OffersContextProvider>
  );
};

export default ({ tracker }) => (
  <Container>
    <PreviewContextProvider>
      <CartContextProvider>
        <ProductsContextProvider>
          <CurrencyContextProvider>
            <App tracker={tracker} />
          </CurrencyContextProvider>
        </ProductsContextProvider>
      </CartContextProvider>
    </PreviewContextProvider>
  </Container>
);
